import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { SectionFrame } from "../atoms/SectionFrame";
import { TID } from "../../lib/testIds";

// ────────────────────────────────────────────────────────────────
// Single swappable accent. Set to lime today; flip to "#ffb020" for amber.
// ────────────────────────────────────────────────────────────────
const ACCENT = "#D8FF3D";
const ACCENT_SOFT = "rgba(216, 255, 61, 0.35)";


// Sequence of lines printed in the terminal
const LINES = [
    { kind: "prompt", text: "./diagnose" },
    { kind: "blank" },
    { kind: "question", text: "Launching something new?" },
    { kind: "question", text: "Do people understand your product?" },
    { kind: "question", text: "Are employees skipping training?" },
    { kind: "question", text: "Need people to remember it?" },
    { kind: "blank" },
    { kind: "final", text: "Let's fix that." },
];

// Per-character speed + per-line delay (ms)
const CHAR_MS = 24;
const LINE_PAUSE_MS = 320;
// How long the completed sequence stays on screen before auto-restarting (ms)
const RESTART_HOLD_MS = 3200;

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const update = () => setReduced(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
        // Mount-once: mq and update are created inside the effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return reduced;
}

export function InteractiveQuestionsSection() {
    return (
        <SectionFrame innerClassName="">
            <div className="relative h-screen min-h-[640px] w-full overflow-hidden flex items-center justify-center px-4 sm:px-6 md:px-10"
                 style={{ background: "#0a0b0a" }}>
                {/* Ambient glows — opposite corners */}
                <div
                    className="absolute pointer-events-none"
                    style={{
                        top: "-10%",
                        left: "-10%",
                        width: "60%",
                        height: "60%",
                        background:
                            "radial-gradient(circle at 30% 30%, rgba(216,255,61,0.10) 0%, rgba(216,255,61,0.03) 35%, transparent 65%)",
                        filter: "blur(40px)",
                    }}
                />
                <div
                    className="absolute pointer-events-none"
                    style={{
                        bottom: "-10%",
                        right: "-10%",
                        width: "55%",
                        height: "55%",
                        background:
                            "radial-gradient(circle at 70% 70%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 40%, transparent 70%)",
                        filter: "blur(50px)",
                    }}
                />

                <Terminal />
            </div>
        </SectionFrame>
    );
}

function Terminal() {
    const reduced = usePrefersReducedMotion();
    const [runId, setRunId] = useState(0);
    const [paused, setPaused] = useState(false);
    // Only start (and tick) when the terminal is on screen.
    // `once: false` so we also freeze when scrolled away.
    const rootRef = useRef(null);
    const inView = useInView(rootRef, { amount: 0.25, margin: "0px 0px -10% 0px" });
    // Once it's been seen at least once, we keep allowing the loop to run
    // even on brief out-of-view dips (auto-restart still works).
    const hasEnteredRef = useRef(false);
    useEffect(() => { if (inView) hasEnteredRef.current = true; }, [inView]);

    // Auto-restart: TerminalRun calls onComplete when the CTA has been visible
    // long enough; we bump runId to remount the run.
    const handleComplete = () => {
        if (!paused) setRunId((n) => n + 1);
    };

    return (
        <div
            ref={rootRef}
            data-testid={TID.questionPanel(0)}
            className="relative w-full max-w-[860px] rounded-[18px] sm:rounded-[22px] overflow-hidden"
            style={{
                background: "rgba(14, 15, 14, 0.92)",
                border: "1px solid rgba(255,255,255,0.07)",
                boxShadow:
                    "0 30px 80px -20px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 60px rgba(216,255,61,0.04)",
                backdropFilter: "blur(8px)",
            }}
        >
            {/* Title bar */}
            <div
                className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b"
                style={{ borderColor: "rgba(255,255,255,0.06)" }}
            >
                <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(255,255,255,0.18)" }} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(255,255,255,0.18)" }} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(255,255,255,0.18)" }} />
                </div>
                <div
                    className="flex-1 text-center text-sm sm:text-base tracking-wider"
                    style={{
                        color: "rgba(255,255,255,0.42)",
                        fontFamily: "'IBM Plex Mono', monospace",
                    }}
                >
                    growco-motion ~ diagnostic
                </div>
                <button
                    type="button"
                    onClick={() => setPaused((p) => !p)}
                    aria-label={paused ? "Play" : "Pause"}
                    aria-pressed={paused}
                    data-testid="terminal-pause"
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-white/5 transition-colors"
                    style={{ color: paused ? ACCENT : "rgba(255,255,255,0.45)" }}
                >
                    {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                </button>
            </div>

            {/* Terminal body */}
            <div
                className="px-5 sm:px-7 md:px-9 py-7 sm:py-9 md:py-11"
                style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    minHeight: "clamp(360px, 56vh, 540px)",
                }}
            >
                <TerminalRun
                    key={runId}
                    reduced={reduced}
                    paused={paused}
                    active={inView || hasEnteredRef.current}
                    onComplete={handleComplete}
                />
            </div>
        </div>
    );
}

function useTypewriter(plan, { reduced, paused, active, onComplete }) {
    const [elapsed, setElapsed] = useState(0);
    const [done, setDone] = useState(false);
    const totalDuration = plan[plan.length - 1].end + 200;
    // Refs so the rAF loop reads always-current values without re-running the effect.
    const elapsedRef = useRef(0);
    const pausedRef = useRef(paused);
    const activeRef = useRef(active);
    useEffect(() => { pausedRef.current = paused; }, [paused]);
    useEffect(() => { activeRef.current = active; }, [active]);

    // Drive the typewriter — single rAF loop for the lifetime of this run.
    // Reads pausedRef/activeRef so it never needs those as dependencies.
    useEffect(() => {
        if (reduced) {
            setElapsed(totalDuration);
            setDone(true);
            elapsedRef.current = totalDuration;
            return;
        }
        let rafId;
        let last = performance.now();
        const step = (now) => {
            const dt = now - last;
            last = now;
            if (!pausedRef.current && activeRef.current) {
                const next = Math.min(elapsedRef.current + dt, totalDuration);
                elapsedRef.current = next;
                setElapsed(next);
                if (next >= totalDuration) {
                    setDone(true);
                    return; // stop loop; auto-restart effect will take over
                }
            }
            rafId = requestAnimationFrame(step);
        };
        rafId = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafId);
    }, [reduced, totalDuration]);

    // Auto-restart after the sequence completes + hold period.
    useEffect(() => {
        if (!done || reduced) return;
        if (pausedRef.current || !activeRef.current) return;
        const id = setTimeout(() => {
            if (!pausedRef.current && activeRef.current && typeof onComplete === "function") {
                onComplete();
            }
        }, RESTART_HOLD_MS);
        return () => clearTimeout(id);
    }, [done, reduced, onComplete, paused, active]);

    return { elapsed, done };
}

function TerminalRun({ reduced, paused, active = true, onComplete }) {
    // LINES / CHAR_MS / LINE_PAUSE_MS are module constants — safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const plan = useMemo(() => {
        let acc = 0;
        return LINES.map((l) => {
            const start = acc;
            const length = l.text ? l.text.length : 0;
            acc += length * CHAR_MS + LINE_PAUSE_MS;
            return { ...l, start, end: start + length * CHAR_MS };
        });
    }, []);

    const { elapsed, done } = useTypewriter(plan, { reduced, paused, active, onComplete });

    return (
        <div className="space-y-1 sm:space-y-1.5">
            {plan.map((l, idx) => (
                <TerminalLine key={`${l.kind}-${idx}`} line={l} idx={idx} elapsed={elapsed} />
            ))}
            <BookDemoCta done={done} />
        </div>
    );
}

function TerminalLine({ line: l, idx, elapsed }) {
                const charsToShow = Math.max(
                    0,
                    Math.min(l.text?.length ?? 0, Math.floor((elapsed - l.start) / CHAR_MS)),
                );
                const isActive = elapsed >= l.start && elapsed < l.end;
                const isFinished = elapsed >= l.end;
                const visible = isActive || isFinished;
                const isFinalLine = l.kind === "final";

                if (l.kind === "blank") {
                    return <div key={`blank-${idx}`} className="h-3 sm:h-4" aria-hidden />;
                }

                if (l.kind === "prompt") {
                    return (
                        <Line
                            key={`prompt-${idx}`}
                            isActive={isActive}
                            visible={visible}
                            color="rgba(255,255,255,0.38)"
                            size="clamp(0.85rem, 1.05vw, 1rem)"
                        >
                            <span style={{ color: "rgba(255,255,255,0.32)" }}>$ </span>
                            {l.text.slice(0, charsToShow)}
                        </Line>
                    );
                }

                if (l.kind === "question") {
                    return (
                        <Line
                            key={`question-${idx}`}
                            isActive={isActive}
                            visible={visible}
                            color="rgba(236, 236, 236, 0.86)"
                            size="clamp(1rem, 1.6vw, 1.35rem)"
                            weight={400}
                            data-testid={TID.questionPanel(idx)}
                        >
                            {l.text.slice(0, charsToShow)}
                        </Line>
                    );
                }

                if (isFinalLine) {
                    const partial = l.text.slice(0, charsToShow);
                    return (
                        <div key={`final-${idx}`} className="pt-3 sm:pt-4">
                            <Line
                                isActive={isActive}
                                visible={visible}
                                color={ACCENT}
                                size="clamp(1.6rem, 4.2vw, 2.6rem)"
                                weight={600}
                                glow
                            data-testid="terminal-final-line"
                            >
                                {partial}
                            </Line>
                            {/* Glowing underline draws in after the line finishes */}
                            <motion.div
                                className="mt-3 sm:mt-4 h-[2px] rounded-full origin-left"
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: isFinished ? 1 : 0 }}
                                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
                                style={{
                                    width: "min(280px, 70%)",
                                    background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`,
                                    boxShadow: `0 0 14px ${ACCENT_SOFT}, 0 0 36px ${ACCENT_SOFT}`,
                                }}
                            />
                        </div>
                    );
                }
                return null;
}

function BookDemoCta({ done }) {
    return (
        <AnimatePresence>
            {done && (
                <motion.div
                    key="cta"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                    className="pt-7 sm:pt-8"
                >
                    <a
                        href="#contact"
                        onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                        data-testid="terminal-book-call"
                        className="inline-flex items-center gap-3 rounded-full px-6 py-3 sm:px-7 sm:py-3.5 text-base sm:text-base uppercase tracking-[0.2em] font-medium transition-transform hover:-translate-y-0.5"
                        style={{
                            color: "#0a0b0a",
                            background: ACCENT,
                            boxShadow: `0 0 24px ${ACCENT_SOFT}, 0 0 60px ${ACCENT_SOFT}`,
                            fontFamily: "'IBM Plex Mono', monospace",
                        }}
                    >
                        <span>▸</span>
                        <span>Book a demo</span>
                    </a>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function Line({ children, isActive, visible, color, size, weight = 400, glow = false, ...rest }) {
    if (!visible) {
        // Reserve vertical space so the layout doesn't jump as lines stream in
        return (
            <div
                style={{ fontSize: size, lineHeight: 1.4, fontWeight: weight, visibility: "hidden" }}
                {...rest}
            >
                .
            </div>
        );
    }
    return (
        <div
            style={{
                color,
                fontSize: size,
                lineHeight: 1.4,
                fontWeight: weight,
                fontFamily: "'IBM Plex Mono', monospace",
                textShadow: glow ? `0 0 18px ${ACCENT_SOFT}` : "none",
                letterSpacing: glow ? "-0.01em" : "0",
            }}
            {...rest}
        >
            {children}
            {isActive && <Caret color={color} />}
        </div>
    );
}

function Caret({ color }) {
    return (
        <span
            aria-hidden
            className="inline-block align-baseline ml-0.5"
            style={{
                width: "0.55em",
                height: "1em",
                background: color,
                opacity: 0.85,
                animation: "gc-caret-blink 1s steps(2, end) infinite",
                transform: "translateY(2px)",
            }}
        >
            <style>{`@keyframes gc-caret-blink { 0%,49% { opacity: 0.85 } 50%,100% { opacity: 0 } }`}</style>
        </span>
    );
}
